import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const NotificationContext = createContext({});

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
};

// Reducer
const initialState = {
  notifications: [],
  unreadCount: 0,
  isOpen: false,
  activeTab: 'all',
  toasts: [],
  loading: false,
};

function notificationReducer(state, action) {
  switch (action?.type) {
    case 'SET_LOADING':
      return { ...state, loading: action?.payload };

    case 'SET_NOTIFICATIONS': {
      const notifications = action?.payload;
      const unreadCount = notifications?.filter(n => !n?.is_read)?.length;
      return { ...state, notifications, unreadCount, loading: false };
    }

    case 'ADD_NOTIFICATION': {
      const exists = state?.notifications?.some(n => n?.id === action?.payload?.id);
      if (exists) return state;
      const notifications = [action?.payload, ...state?.notifications];
      const unreadCount = notifications?.filter(n => !n?.is_read)?.length;
      return { ...state, notifications, unreadCount };
    }

    case 'MARK_READ': {
      const notifications = state?.notifications?.map(n =>
        n?.id === action?.payload ? { ...n, is_read: true } : n
      );
      const unreadCount = notifications?.filter(n => !n?.is_read)?.length;
      return { ...state, notifications, unreadCount };
    }

    case 'MARK_ALL_READ': {
      const notifications = state?.notifications?.map(n => ({ ...n, is_read: true }));
      return { ...state, notifications, unreadCount: 0 };
    }

    case 'DELETE_NOTIFICATION': {
      const notifications = state?.notifications?.filter(n => n?.id !== action?.payload);
      const unreadCount = notifications?.filter(n => !n?.is_read)?.length;
      return { ...state, notifications, unreadCount };
    }

    case 'TOGGLE_PANEL':
      return { ...state, isOpen: !state?.isOpen };

    case 'CLOSE_PANEL':
      return { ...state, isOpen: false };

    case 'SET_TAB':
      return { ...state, activeTab: action?.payload };

    case 'ADD_TOAST': {
      const toasts = [...state?.toasts, action?.payload]?.slice(-3);
      return { ...state, toasts };
    }

    case 'REMOVE_TOAST':
      return { ...state, toasts: state?.toasts?.filter(t => t?.id !== action?.payload) };

    default:
      return state;
  }
}

export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { user } = useAuth();
  const channelRef = useRef(null);
  const broadcastChannelRef = useRef(null);
  const toastTimersRef = useRef({});

  // Fetch initial notifications
  const fetchNotifications = useCallback(async (userId) => {
    if (!userId) return;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(50);
      if (!error && data) {
        dispatch({ type: 'SET_NOTIFICATIONS', payload: data });
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } catch (err) {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Persist notification to DB
  const persistNotification = useCallback(async (notification, userId) => {
    if (!userId) return;
    try {
      await supabase?.from('notifications')?.insert({
        id: notification?.id,
        user_id: userId,
        type: notification?.type,
        title: notification?.title,
        message: notification?.message,
        is_read: false,
        severity: notification?.severity || 'low',
        metadata: notification?.metadata || {},
        created_at: notification?.created_at || new Date()?.toISOString(),
      });
    } catch (err) {
      // Silent fail for persistence
    }
  }, []);

  // Show toast
  const showToast = useCallback((notification) => {
    const toastId = notification?.id + '_toast';
    dispatch({ type: 'ADD_TOAST', payload: { ...notification, toastId } });

    const isPersistent = notification?.severity === 'high' || notification?.severity === 'critical';
    if (!isPersistent) {
      toastTimersRef.current[toastId] = setTimeout(() => {
        dispatch({ type: 'REMOVE_TOAST', payload: toastId });
        delete toastTimersRef?.current?.[toastId];
      }, 5000);
    }
  }, []);

  const dismissToast = useCallback((toastId) => {
    if (toastTimersRef?.current?.[toastId]) {
      clearTimeout(toastTimersRef?.current?.[toastId]);
      delete toastTimersRef?.current?.[toastId];
    }
    dispatch({ type: 'REMOVE_TOAST', payload: toastId });
  }, []);

  // Handle incoming notification (from broadcast or DB change)
  const handleIncomingNotification = useCallback((notification, userId) => {
    dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
    showToast(notification);
    persistNotification(notification, userId);
  }, [showToast, persistNotification]);

  // Setup Supabase realtime subscriptions
  useEffect(() => {
    if (!user?.id) {
      // Cleanup on logout
      if (channelRef?.current) {
        supabase?.removeChannel(channelRef?.current);
        channelRef.current = null;
      }
      if (broadcastChannelRef?.current) {
        supabase?.removeChannel(broadcastChannelRef?.current);
        broadcastChannelRef.current = null;
      }
      dispatch({ type: 'SET_NOTIFICATIONS', payload: [] });
      return;
    }

    fetchNotifications(user?.id);

    // Subscribe to postgres_changes for notifications table (user's own)
    const dbChannel = // Subscribe to user_profiles UPDATE (plan/subscription changes)
    // Subscribe to fraud_alerts INSERT/UPDATE
    // Subscribe to partner_profiles UPDATE (status changes)
    // Subscribe to transactions INSERT
    supabase?.channel(`notifications_db_${user?.id}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user?.id}`,
        },
        (payload) => {
          if (payload?.new) {
            dispatch({ type: 'ADD_NOTIFICATION', payload: payload?.new });
            showToast(payload?.new);
          }
        }
      )?.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'transactions' },
        (payload) => {
          if (!payload?.new) return;
          const t = payload?.new;
          const notification = {
            id: `txn_${t?.id}_${Date.now()}`,
            type: 'transaction',
            title: 'New transaction logged',
            message: `Transaction of $${parseFloat(t?.amount || 0)?.toFixed(2)} at ${t?.merchant_name || t?.description || 'Unknown'}`,
            severity: 'low',
            is_read: false,
            created_at: new Date()?.toISOString(),
            metadata: { amount: t?.amount, merchant: t?.merchant_name || t?.description },
          };
          handleIncomingNotification(notification, user?.id);
        }
      )?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'partner_profiles' },
        (payload) => {
          if (!payload?.new) return;
          const p = payload?.new;
          const old = payload?.old || {};
          if (p?.status === old?.status) return;
          let title = 'Partner status updated';
          let message = `Partner ${p?.company_name || p?.business_name || 'Unknown'} status changed to ${p?.status}`;
          if (p?.status === 'approved' || p?.status === 'active') {
            title = 'Partner Application Approved';
            message = `Partner application approved: ${p?.company_name || p?.business_name || 'Unknown'} is now active`;
          } else if (p?.status === 'pending') {
            title = 'New Partner Application';
            message = `New partner application pending review: ${p?.company_name || p?.business_name || 'Unknown'}`;
          } else if (p?.status === 'suspended') {
            title = 'Partner Suspended';
            message = `Partner ${p?.company_name || p?.business_name || 'Unknown'} has been suspended`;
          }
          const notification = {
            id: `partner_${p?.id}_${Date.now()}`,
            type: 'partner',
            title,
            message,
            severity: p?.status === 'suspended' ? 'medium' : 'low',
            is_read: false,
            created_at: new Date()?.toISOString(),
            metadata: { company_name: p?.company_name || p?.business_name, status: p?.status },
          };
          handleIncomingNotification(notification, user?.id);
        }
      )?.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'fraud_alerts' },
        (payload) => {
          if (!payload?.new) return;
          const f = payload?.new;
          const severityMap = { critical: 'critical', high: 'high', medium: 'medium', low: 'low' };
          const sev = severityMap?.[f?.severity] || 'medium';
          const notification = {
            id: `fraud_${f?.id}_${Date.now()}`,
            type: 'fraud',
            title: sev === 'critical' ? 'Critical Fraud Flag Raised' : '⚠ Fraud Alert Detected',
            message: sev === 'critical' ?'Critical fraud flag raised — immediate review required'
              : `Fraud alert: ${f?.alert_type || f?.event_type || 'Suspicious activity'} detected`,
            severity: sev,
            is_read: false,
            created_at: new Date()?.toISOString(),
            metadata: { event_type: f?.alert_type || f?.event_type, email: f?.user_email },
          };
          handleIncomingNotification(notification, user?.id);
        }
      )?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'fraud_alerts' },
        (payload) => {
          if (!payload?.new) return;
          const f = payload?.new;
          const old = payload?.old || {};
          if (f?.status === old?.status) return;
          if (f?.status === 'resolved') {
            const notification = {
              id: `fraud_resolved_${f?.id}_${Date.now()}`,
              type: 'fraud',
              title: 'Fraud Alert Resolved',
              message: `Fraud alert resolved: ${f?.alert_type || f?.event_type || 'Suspicious activity'}`,
              severity: 'low',
              is_read: false,
              created_at: new Date()?.toISOString(),
              metadata: { event_type: f?.alert_type || f?.event_type },
            };
            handleIncomingNotification(notification, user?.id);
          }
        }
      )?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'user_profiles' },
        (payload) => {
          if (!payload?.new) return;
          const u = payload?.new;
          const old = payload?.old || {};
          if (u?.plan === old?.plan || u?.subscription_plan === old?.subscription_plan) return;
          const newPlan = u?.plan || u?.subscription_plan;
          const oldPlan = old?.plan || old?.subscription_plan;
          let title = 'Subscription Updated';
          let message = `Your subscription plan has changed to ${newPlan}`;
          if (newPlan === 'premium' || newPlan === 'pro') {
            title = 'Subscription Upgraded!';
            message = `You have been upgraded to ${newPlan?.charAt(0)?.toUpperCase() + newPlan?.slice(1)}!`;
          } else if (!newPlan || newPlan === 'cancelled' || newPlan === 'free') {
            title = 'Subscription Cancelled';
            message = `Subscription cancelled for ${u?.email || 'your account'}`;
          }
          const notification = {
            id: `sub_${u?.id}_${Date.now()}`,
            type: 'subscription',
            title,
            message,
            severity: 'low',
            is_read: false,
            created_at: new Date()?.toISOString(),
            metadata: { plan: newPlan, previous_plan: oldPlan, email: u?.email },
          };
          handleIncomingNotification(notification, user?.id);
        }
      )?.subscribe();

    channelRef.current = dbChannel;

    // Broadcast channel for cross-client notifications
    const broadcastChannel = supabase?.channel('finvisar-notifications')?.on('broadcast', { event: 'notification' }, (payload) => {
        if (!payload?.payload) return;
        const notification = {
          ...payload?.payload,
          id: payload?.payload?.id || `broadcast_${Date.now()}`,
          is_read: false,
          created_at: payload?.payload?.timestamp || new Date()?.toISOString(),
        };
        dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
        showToast(notification);
      })?.subscribe();

    broadcastChannelRef.current = broadcastChannel;

    return () => {
      if (channelRef?.current) {
        supabase?.removeChannel(channelRef?.current);
        channelRef.current = null;
      }
      if (broadcastChannelRef?.current) {
        supabase?.removeChannel(broadcastChannelRef?.current);
        broadcastChannelRef.current = null;
      }
    };
  }, [user?.id, fetchNotifications, handleIncomingNotification, showToast]);

  // Cleanup toast timers on unmount
  useEffect(() => {
    return () => {
      Object.values(toastTimersRef?.current)?.forEach(clearTimeout);
    };
  }, []);

  // Actions
  const markRead = useCallback(async (notificationId) => {
    dispatch({ type: 'MARK_READ', payload: notificationId });
    if (user?.id) {
      await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', notificationId)?.eq('user_id', user?.id);
    }
  }, [user?.id]);

  const markAllRead = useCallback(async () => {
    dispatch({ type: 'MARK_ALL_READ' });
    if (user?.id) {
      await supabase?.from('notifications')?.update({ is_read: true })?.eq('user_id', user?.id)?.eq('is_read', false);
    }
  }, [user?.id]);

  const deleteNotification = useCallback(async (notificationId) => {
    dispatch({ type: 'DELETE_NOTIFICATION', payload: notificationId });
    if (user?.id) {
      await supabase?.from('notifications')?.delete()?.eq('id', notificationId)?.eq('user_id', user?.id);
    }
  }, [user?.id]);

  const togglePanel = useCallback(() => dispatch({ type: 'TOGGLE_PANEL' }), []);
  const closePanel = useCallback(() => dispatch({ type: 'CLOSE_PANEL' }), []);
  const setActiveTab = useCallback((tab) => dispatch({ type: 'SET_TAB', payload: tab }), []);

  // Broadcast a notification to all connected clients
  const broadcastNotification = useCallback(async (notification) => {
    if (!broadcastChannelRef?.current) return;
    await broadcastChannelRef?.current?.send({
      type: 'broadcast',
      event: 'notification',
      payload: {
        ...notification,
        id: notification?.id || `broadcast_${Date.now()}`,
        timestamp: new Date()?.toISOString(),
      },
    });
  }, []);

  const value = {
    ...state,
    markRead,
    markAllRead,
    deleteNotification,
    togglePanel,
    closePanel,
    setActiveTab,
    dismissToast,
    broadcastNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
